import React, { useEffect, useState, useCallback } from 'react';
import {
  ShoppingBag,
  User,
  Home,
  Grid3X3,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import img from './togo.png'
import { getAuth } from 'firebase/auth';
import { useLoginPopup } from '../Auth/LoginContext';
import { auth } from '../Admin/Config/Config';
import AutocompleteWithRefine from '../Home/Search/AutoComplete/AutoComplete';
import { useCart } from '../Home/Product/CartContext';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../Admin/Config/Config';
import company from '../assets/company.png';

const Navbar = ({ user }) => {
  const { cart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const { openPopup } = useLoginPopup();
  const location = useLocation();

  // Fetch categories from Firebase
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const categoryQuery = query(
        collection(db, "category"),
        where("status", "==", true),
        orderBy("order", "asc"),
        limit(20)
      );

      const querySnapshot = await getDocs(categoryQuery);
      const categoriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Add "All Products" as first category and limit to 8 for navbar
      const navbarCategories = [
        { id: 'all-products', category_name: 'All Products', path: '/search-items', isAll: true },
        ...categoriesData.slice(0, 12) // Show only 7 categories + All Products = 8 total
      ];

      setCategories(navbarCategories);
    } catch (err) {
      console.error('Error fetching categories for navbar:', err);
      setCategories([
        { id: 'all-products', category_name: 'All Products', path: '/search-items', isAll: true }
      ]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Check if current route is active
  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  // Check if current category is active
  const isCategoryActive = (category) => {
    if (category.isAll) {
      return false;
    }
    return location.pathname.includes(category.path) ||
      location.search.includes(`f=${encodeURIComponent(category.category_name)}`);
  };

  const currentUser = getAuth().currentUser;

  return (
    <div className="flex mx-auto sticky top-0 z-50 flex-col">
      {/* Modern Navigation with Custom Red Background */}
      <nav
        className={`transition-all duration-500 ease-out relative ${scrolled ? "shadow-lg border-b" : "shadow-md border-b"
          }`}
        style={{
          backgroundColor: scrolled ? "#A01800" : "#BD1D00",
          borderBottomColor: scrolled ? "#8B1600" : "#A01800",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Modern Logo with SVG - Hidden on Mobile */}
            <Link
              to="/"
              className="hidden md:flex items-center space-x-4 group hover-lift"
            >
              <div className="flex items-center space-x-3">
                <img
                  src={img}
                  alt="logo"
                  className="h-12 w-12 lg:h-14 lg:w-14 transition-all duration-300 group-hover:scale-110"
                />
                <div className="hidden lg:block">
                  <img
                    src={company}
                    alt="logo"
                    className="h-12 w-45 lg:h-14 lg:w-45 transition-all duration-300 group-hover:scale-110"
                  />
                  {/* <div className="text-xl font-black tracking-tight text-white group-hover:text-gray-100 transition-all duration-300">
                    TO GO PACKING
                  </div>
                  <div className="text-xs font-semibold tracking-widest  text-white/80">
                    Premium Solutions
                  </div> */}
                </div>
              </div>
            </Link>

            {/* Modern Search Bar with Neumorphism */}
            <div className="flex-grow relative hidden md:block max-w-2xl mx-8">
              <div className="relative group">
                <AutocompleteWithRefine />
              </div>
            </div>

            {/* Modern Desktop Navigation Items */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Modern Cart & Wishlist */}
              <div className="flex space-x-3 items-center">
                <Link
                  to="/cart"
                  className={`modern-nav-item relative flex items-center group px-4 py-3 rounded-2xl border border-slate-50 transition-all duration-300
    ${isActiveRoute("/cart")
                      ? "bg-red-600 border-gray-500 text-white shadow-lg scale-[1.02]"
                      : "bg-red-700 border-gray-600 text-white hover:bg-red-600 hover:border-gray-500 hover:shadow-lg hover:scale-[1.02]"
                    }`}
                >
                  <div className="relative">
                    <ShoppingBag className="h-5 w-5 text-white transition-transform duration-300 group-hover:scale-110" />
                    {cart && cart.length > 0 && (
                      <div
                        className="modern-badge absolute -top-2 -right-2 min-w-[18px] h-[18px] rounded-full flex items-center justify-center 
        text-[10px] font-bold text-white bg-gray-700 border-2 border-white shadow-inner transition-all duration-300 group-hover:bg-gray-600"
                      >
                        {cart.length}
                      </div>
                    )}
                  </div>
                  <span className="text-sm ml-3 font-semibold text-white">
                    Cart
                  </span>
                </Link>
              </div>

              {/* Modern User Authentication */}
              <div className="flex items-center border-l border-gray-200/30 pl-6">
                {!currentUser ? (
                  <button
                    onClick={openPopup}
                    className="modern-signin-btn px-6 py-3  text-white rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300/50 hover:scale-105"
                  >
                    <span className="flex items-center  space-x-2">
                      <User className="w-4 h-4" />
                      <span>Sign In</span>
                    </span>
                  </button>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className={`modern-nav-item relative flex items-center group px-3 py-2 rounded-2xl transition-all duration-300 max-w-48 ${isUserMenuOpen
                        ? "modern-nav-active text-white shadow-lg"
                        : "modern-nav-inactive hover:shadow-md"
                        }`}
                    >
                      <div className="relative">
                        <div className="professional-avatar-ring">
                          <img
                            src={
                              currentUser.photoURL ||
                              "https://uxwing.com/wp-content/themes/uxwing/download/peoples-avatars/default-avatar-profile-picture-male-icon.png"
                            }
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://uxwing.com/wp-content/themes/uxwing/download/peoples-avatars/default-avatar-profile-picture-male-icon.png";
                            }}
                            alt="Profile"
                            className="h-10 w-10 rounded-full object-cover transition-all duration-300 group-hover:scale-105"
                          />
                        </div>
                        <div className="professional-status-indicator absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white shadow-sm"></div>
                      </div>
                      <div className="text-left flex-1 min-w-0 ml-3">
                        <div className="text-sm font-semibold text-white truncate group-hover:text-gray-100 transition-colors duration-300">
                          {user?.name || currentUser.displayName || "User"}
                        </div>
                        <div className="text-xs text-white/70 truncate group-hover:text-white/80 transition-colors duration-300">
                          {currentUser.email}
                        </div>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-white/60 transition-all duration-300 group-hover:text-white/80 ${isUserMenuOpen ? "rotate-180" : ""
                          }`}
                      />
                    </button>

                    {isUserMenuOpen && (
                      <div className="professional-dropdown absolute right-0 mt-4 w-72 backdrop-blur-xl bg-white/95 rounded-2xl shadow-2xl border border-gray-200/60 overflow-hidden z-50">
                        {/* Professional Header */}
                        <div className="professional-dropdown-header px-6 py-5 bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200/60">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <img
                                src={
                                  currentUser.photoURL ||
                                  "https://uxwing.com/wp-content/themes/uxwing/download/peoples-avatars/default-avatar-profile-picture-male-icon.png"
                                }
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://uxwing.com/wp-content/themes/uxwing/download/peoples-avatars/default-avatar-profile-picture-male-icon.png";
                                }}
                                alt="Profile"
                                className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-md"
                              />
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white"></div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-base font-semibold text-gray-900 truncate">
                                {user?.name ||
                                  currentUser.displayName ||
                                  "User"}
                              </div>
                              <div className="text-sm text-gray-600 truncate">
                                {currentUser.email}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Professional Menu Items */}
                        <div className="py-2">
                          <Link
                            to="/profile"
                            className="professional-dropdown-item flex items-center space-x-3 px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 group"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-red-100 transition-colors duration-200">
                              <User className="w-4 h-4 text-gray-600 group-hover:text-red-600" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">
                                Account Settings
                              </div>
                              <div className="text-xs text-gray-500">
                                Manage your profile
                              </div>
                            </div>
                          </Link>

                          <div className="mx-6 my-2 border-t border-gray-200/60"></div>

                          <button
                            onClick={() => {
                              auth.signOut();
                              setIsUserMenuOpen(false);
                            }}
                            className="professional-dropdown-item flex items-center space-x-3 w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-red-50 transition-all duration-200 group"
                          >
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-red-100 transition-colors duration-200">
                              <X className="w-4 h-4 text-gray-600 group-hover:text-red-600" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium group-hover:text-red-700">
                                Sign Out
                              </div>
                              <div className="text-xs text-gray-500">
                                End your session
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Layout: Hamburger Left, Name Center, Cart Right */}
            <div className="flex items-center md:hidden w-full">
              {/* Hamburger Menu - Left */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`modern-menu-btn p-3 rounded-2xl transition-all duration-300 focus:outline-none shadow-lg ${isOpen ? "modern-menu-active" : "modern-menu-inactive"
                  }`}
              >
                {isOpen ? (
                  <X className="w-6 h-6 transition-transform duration-300 rotate-180 text-white" />
                ) : (
                  <Menu className="w-6 h-6 transition-transform duration-300 text-white" />
                )}
              </button>

              {/* Company Name - Center */}
              <div className="flex-1 flex justify-center">
                <Link to="/" className="flex items-center space-x-2 text-white">
                  <img
                    src={img}
                    alt="TO GO PACKING logo"
                    className="h-5 w-5 sm:h-6 sm:w-6"
                  />
                  <span className="text-base font-bold sm:text-lg text-white">
                    <img
                      src={company}
                      alt="logo"
                      className="h-8 w-18 lg:h-14 lg:w-45 transition-all duration-300 group-hover:scale-110"
                    />
                  </span>
                </Link>
              </div>

              {/* Cart Icon - Right */}
              <Link
                to="/cart"
                className="relative flex items-center p-3 rounded-2xl transition-all duration-300 text-white"
              >
                <ShoppingBag className="h-6 w-6 text-white" />
                {cart && cart.length > 0 && (
                  <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-yellow-500 rounded-full flex items-center justify-center text-xs font-bold text-black border-2 border-white shadow-lg">
                    {cart.length}
                  </div>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Categories Bar - Desktop Only */}
        <div className="hidden md:block border-t border-white/20 bg-white/5 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-12">
              {/* Categories Label */}
              <div className="flex items-center space-x-2 text-white font-semibold text-sm pr-6 border-r border-white/20">
                <Grid3X3 className="w-4 h-4" />
                <span>Categories</span>
              </div>

              {/* Categories List */}
              <div className="flex-1 flex items-center space-x-6 overflow-x-auto scrollbar-hide px-4">
                {categoriesLoading
                  ? // Loading skeleton
                  Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="flex-shrink-0">
                      <div className="h-6 bg-white/20 rounded animate-pulse w-20"></div>
                    </div>
                  ))
                  : categories.map((category) => (
                    <Link
                      key={category.id}
                      to={
                        category.isAll
                          ? `/search-items`
                          : `/search-items?query=${encodeURIComponent(
                            category.category_name
                          )}&f=${category.category_name}`
                      }
                      className={`whitespace-nowrap text-sm font-medium transition-all duration-300 hover:text-white py-1 border-b-2 ${isCategoryActive(category)
                        ? "text-white border-white"
                        : "text-white/80 border-transparent hover:border-white/50"
                        }`}
                      onClick={() => {
                        if (category.isAll) {
                          window.location.href = `/search-items`;
                        }
                      }}
                    >
                      {category.category_name}
                    </Link>
                  ))}
              </div>

              {/* View All Categories Link */}
              <Link
                to="/categories"
                className="flex items-center space-x-1 text-white/80 hover:text-white transition-colors duration-300 text-sm font-medium pl-6 border-l border-white/20"
              >
                <span>View All</span>
                <ChevronDown className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Modern Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-500 ease-out ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
            } overflow-hidden`}
        >
          <div
            className="modern-mobile-menu shadow-2xl"
            style={{ backgroundColor: "#A01800", borderTopColor: "#8B1600" }}
          >
            <div className="px-6 py-6 space-y-4">
              {/* Mobile Search */}
              <div className="mb-6">
                <div className="modern-mobile-search bg-white/10 border border-white/20 rounded-2xl shadow-lg">
                  <AutocompleteWithRefine />
                </div>
              </div>

              {/* Modern Mobile Navigation Links */}
              {[
                { to: "/", label: "Home", icon: Home },
                {
                  to: "/cart",
                  label: "Shopping Cart",
                  icon: ShoppingBag,
                  badge: cart?.length,
                },
                { to: "/categories", label: "Categories", icon: Grid3X3 },
                { to: "/profile", label: "Profile", icon: User },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`modern-mobile-nav-item flex items-center justify-between p-4 rounded-2xl transition-all duration-300 ${isActiveRoute(item.to)
                    ? "modern-mobile-nav-active text-white shadow-lg"
                    : "modern-mobile-nav-inactive hover:shadow-md"
                    }`}
                >
                  <div className="flex items-center space-x-4">
                    <item.icon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110 text-white" />
                    <span className="font-semibold text-white">
                      {item.label}
                    </span>
                  </div>
                  {item.badge > 0 && (
                    <div className="modern-mobile-badge min-w-[22px] h-6 rounded-full flex items-center justify-center text-xs font-bold text-white bg-yellow-500 shadow-lg">
                      {item.badge}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Modern Bottom Navigation - Mobile */}
      <div className="modern-bottom-nav fixed bottom-0 left-0 right-0 backdrop-blur-2xl bg-white/90 shadow-2xl md:hidden border-t border-gray-200/50 z-40">
        <div className="flex justify-around items-center h-20 px-2">
          {[
            { to: "/", icon: Home, label: "Home" },
            {
              to: "/cart",
              icon: ShoppingBag,
              label: "Cart",
              badge: cart?.length,
            },
            { to: "/categories", icon: Grid3X3, label: "Categories" },
            { to: "/profile", icon: User, label: "Profile" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="modern-bottom-nav-item relative flex flex-col items-center group min-w-0 flex-1 py-3"
            >
              <div
                className={`modern-bottom-icon relative p-3 rounded-2xl transition-all duration-300 border ${isActiveRoute(item.to)
                  ? "bg-black border-black text-white shadow-lg scale-105"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100 hover:shadow-md"
                  }`}
              >
                <item.icon
                  className={`w-5 h-5 transition-all duration-300 ${isActiveRoute(item.to)
                    ? "text-white"
                    : "text-gray-600 group-hover:text-black"
                    }`}
                />
                {item.badge > 0 && (
                  <div className="modern-bottom-badge absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full flex items-center justify-center text-xs font-bold text-black bg-yellow-400 border border-white shadow">
                    {item.badge}
                  </div>
                )}
              </div>
              <span
                className={`modern-bottom-label text-xs mt-2 font-semibold transition-all duration-300 truncate max-w-full ${isActiveRoute(item.to)
                  ? "text-black"
                  : "text-gray-600 group-hover:text-black"
                  }`}
              >
                {item.label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Add scrollbar hide utility to existing styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* ... rest of your existing styles ... */
      `}</style>
    </div>
  );
};

export default Navbar;